import {
  DEFAULT_GEMINI_LANGUAGE,
  GEMINI_LANGUAGE_OPTIONS,
  normalizeGeminiLanguage,
} from './openExternalAi';

/** Default language for newly created canvases (and non-canvas pages). */
export const GEMINI_LANGUAGE_STORAGE_KEY = 'hashely_canvas_gemini_language';
/** Frozen snapshot of the pre-per-canvas global preference for seeding existing boards. */
export const GEMINI_LANGUAGE_LEGACY_FALLBACK_KEY = 'hashely_canvas_gemini_language_legacy_fallback';
export const GEMINI_LANGUAGE_CHANGE_EVENT = 'hashely-canvas-gemini-language';

function isValidGeminiLanguage(language) {
  return GEMINI_LANGUAGE_OPTIONS.some(option => option.code === language);
}

export function readDefaultGeminiLanguage() {
  try {
    const stored = window.localStorage?.getItem(GEMINI_LANGUAGE_STORAGE_KEY);
    if (stored && isValidGeminiLanguage(stored)) {
      return stored;
    }
  } catch (err) {
    console.warn('Failed to load Gemini language preference', err);
  }
  return DEFAULT_GEMINI_LANGUAGE;
}

export function writeDefaultGeminiLanguage(nextLanguage) {
  const normalized = normalizeGeminiLanguage(nextLanguage);
  try {
    window.localStorage?.setItem(GEMINI_LANGUAGE_STORAGE_KEY, normalized);
  } catch (err) {
    console.warn('Failed to save Gemini language preference', err);
  }
  return normalized;
}

/**
 * One-time freeze of the old global language so existing canvases without
 * board.locale keep their previous language when the default for new canvases changes.
 */
export function readLegacyGeminiLanguageFallback() {
  try {
    const frozen = window.localStorage?.getItem(GEMINI_LANGUAGE_LEGACY_FALLBACK_KEY);
    if (frozen && isValidGeminiLanguage(frozen)) {
      return frozen;
    }
    const legacy = readDefaultGeminiLanguage();
    window.localStorage?.setItem(GEMINI_LANGUAGE_LEGACY_FALLBACK_KEY, legacy);
    return legacy;
  } catch (err) {
    console.warn('Failed to load legacy Gemini language fallback', err);
  }
  return DEFAULT_GEMINI_LANGUAGE;
}

export function resolveSeedGeminiLanguage(board) {
  const fingerprintLocale = board?.contextRecommendations?.sourceFingerprint?.locale;
  if (fingerprintLocale && isValidGeminiLanguage(fingerprintLocale)) {
    return fingerprintLocale;
  }
  return readLegacyGeminiLanguageFallback();
}

export function dispatchGeminiLanguageChange({ language, hashtag = null }) {
  try {
    window.dispatchEvent(
      new CustomEvent(GEMINI_LANGUAGE_CHANGE_EVENT, {
        detail: { language, hashtag },
      })
    );
  } catch (err) {
    // Ignore environments without CustomEvent.
  }
}

export { isValidGeminiLanguage };
